import { Button } from "components/common";
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useEffect, useMemo, useState } from 'react';
import { ProductDetail } from 'types/models/product';

const ProductBadge = ({ isPremium = false }: { isPremium?: boolean }) => {
  const premiumTextStyle = {
    background: 'linear-gradient(90deg, var(--primitive-blue-200, #8D8AFF) 0%, var(--primitive-pink-200, #FF6FAA) 50%, var(--primitive-orange-200, #FFB96D) 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  return (
    <div className="typography-caption-head w-fit rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
      {isPremium ? <span style={premiumTextStyle}>Premium</span> : 'Best'}
    </div>
  );
};

const ProductItem = ({ product, onClick, isPremium = false, bonusRatio = 0 }: { product: ProductDetail; onClick: () => void; isPremium?: boolean; bonusRatio?: number }) => {
  const { productId, amount, price } = product || {};
  const coinUrl = PageShop.getCoinURLByOrder(productId);

  return (
    <div className="flex flex-col overflow-hidden rounded-xl" onClick={onClick}>
      <div className="flex flex-1 bg-base-bg-subtle px-8 py-2">
        <img src={coinUrl} alt="" style={{ width: '96px', height: '96px' }} />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center bg-base-bg-strong pb-4 pt-2 text-center">
        <ProductBadge isPremium={isPremium} />
        <div className="typography-headline-md my-1">
          {amount} Coins
          {bonusRatio > 0 ? <span> +{amount * (bonusRatio / 100)}</span> : ''}
        </div>
        <div className="typography-body-sm text-base-txt-secondary">{price}</div>
      </div>
    </div>
  );
};

const NeedCoinContent = () => {
  const { sheets, close } = useBottomSheet();
  const sheet = sheets.NeedCoin;
  const { productIds, premiumProductId, showShopBtn = true, bonusRatio = 0, isSearchBonusPromo = false, body } = sheet.data;
  const [products, setProducts] = useState<any[]>([]);

  const title = brsc.low_coins_title;
  const content = useMemo<string>(() => {
    if(isSearchBonusPromo && bonusRatio > 0) {
      return brsc.coin_topup_bonus_10_body.replace('10%', `${bonusRatio}%`);
    }

    if(body) {
      return body;
    }
    
    return brsc.how_pay2_gender2.replace('.', '.<br />');
  },[bonusRatio, isSearchBonusPromo, body]);

  useEffect(() => {
    if (!productIds) return;

    setProducts(productIds.map((productId: string) => PageShop.getProductDetails(productId)));
  }, [productIds]);

  useEffect(() => {
    PurchaseManager.attach((err, coin, productId) => {
      if (err) {
        return;
      }

      close(sheet.id, { data: productId });

      if(isSearchBonusPromo) {
        ba('search_promo', undefined, {
          fbUid: myinfo.fbUid,
          country: myinfo.country,
          gender: myinfo.gender,
          actionType: 'purchase-success',
          productId: productId,
          bonusRatio: bonusRatio,
          randomNum: 3,
          lastPurchaseAmount: window.lastPurchaseInfo?.amount ?? 0,
          roomState: RoomUI.getState(),
        });
      }
    });

    return () => {
      PurchaseManager.detach();
      PageShop.resetPromo();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToShop = () => {
    close(sheet.id);
    Transition.showPage(Def.PAGE_SHOP);
  };

  const buyCoin = (productId: string) => {
    if (bonusRatio > 0) {
      Braze.applyPageShopProtocol(new URL(`thebermuda://deeplink/promo?bonus_ratio=${bonusRatio}`));
    }
    
    PurchaseManager.buyCoin(productId);
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="typography-headline-md">{title}</div>
      <div className="typography-headline-sm text-center text-base-txt-secondary" dangerouslySetInnerHTML={{ __html: content }} />
      <div className="h-4" />
      <div className="align-center flex gap-2 py-4">
        {products.map((product) => (
          <ProductItem
            key={product?.productId}
            product={product}
            onClick={() => buyCoin(product?.productId)}
            isPremium={product?.productId === premiumProductId}
            bonusRatio={bonusRatio}
          />
        ))}
      </div>
      <div className="mt-2 flex w-full justify-center gap-3">
        {showShopBtn && (
          <Button
            onClick={goToShop}
            className="flex-1"
            style={{ text: brsc.go_to_shop, color: 'btn-bg-video-primary', fontColor: 'btn-txt-video-primary', borderRadius: 'full' }}
          />
        )}
        <Button
          onClick={() => close(sheet.id)}
          className="w-fit flex-none px-4"
          style={{ text: '', icon: { name: '/icons/ic_close.svg', size: 28, color: 'filter-base-ic-white' }, color: 'btn-bg-normal', borderRadius: 'lg' }}
        />
      </div>
    </div>
  );
};

export default NeedCoinContent;